import React, { useState } from 'react';
import {
  BookOpen,
  Calendar,
  Clock,
  Target,
  FileText,
  ChevronDown,
  ChevronRight,
  Users,
  Lightbulb,
  CheckSquare,
  GraduationCap,
  Download,
  Printer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { primaryCurriculumData, getSchemeOfWork, getDailyLessons } from '@/data/primaryCurriculumData';

// Primary School Scheme of Work Component
export const PrimarySchemeOfWork = ({ subject, year, schoolName = "Primary School" }) => {
  const [selectedTerm, setSelectedTerm] = useState('Term 1');
  const [expandedWeek, setExpandedWeek] = useState(null);

  // Get curriculum data
  const subjectData = primaryCurriculumData[subject?.toLowerCase()];
  const yearData = subjectData?.[`year${year}`];
  
  if (!yearData) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-muted-foreground">Curriculum data for {subject} Year {year} is being developed.</p>
          <p className="text-sm text-gray-400 mt-2">Check back soon!</p>
        </CardContent>
      </Card>
    );
  }

  // Term data is computed inside each tab's render below

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-xl text-green-800">
                {subject} - Year {year} Scheme of Work
              </CardTitle>
              <CardDescription className="text-green-600">
                {schoolName} • {yearData.periodsPerWeek} periods/week • {yearData.totalWeeks} weeks/year
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" className="gap-2">
              <Printer className="w-4 h-4" />
              Print Scheme
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Term Tabs */}
      <Tabs value={selectedTerm} onValueChange={setSelectedTerm}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="Term 1" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            Term 1
          </TabsTrigger>
          <TabsTrigger value="Term 2" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            Term 2
          </TabsTrigger>
          <TabsTrigger value="Term 3" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            Term 3
          </TabsTrigger>
          <TabsTrigger value="Term 4" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            Term 4
          </TabsTrigger>
        </TabsList>

        {['Term 1', 'Term 2', 'Term 3', 'Term 4'].map(term => {
          const thisTermData = yearData.terms?.find(t => t.term === term);
          const thisScheme = thisTermData?.scheme || [];
          return (
          <TabsContent key={term} value={term}>
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-lg">{term} - {thisTermData?.focus || 'Curriculum Focus'}</CardTitle>
                    <CardDescription>10 Weeks • Click on a week to see daily lesson plans</CardDescription>
                  </div>
                  <Badge className="bg-green-100 text-green-800">
                    {thisScheme.length} Topics
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {thisScheme.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-50">
                          <TableHead className="w-20 font-bold">WEEK</TableHead>
                          <TableHead className="font-bold">TOPIC</TableHead>
                          <TableHead className="font-bold">LEARNING OUTCOMES</TableHead>
                          <TableHead className="font-bold">ACTIVITIES</TableHead>
                          <TableHead className="w-24 text-center font-bold">LESSONS</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {thisScheme.map((weekData) => (
                          <React.Fragment key={weekData.week}>
                            <TableRow 
                              className="cursor-pointer hover:bg-green-50 transition-colors"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setExpandedWeek(expandedWeek === weekData.week ? null : weekData.week);
                              }}
                            >
                              <TableCell className="font-bold text-green-700">
                                <div className="flex items-center gap-2">
                                  {expandedWeek === weekData.week ? 
                                    <ChevronDown className="w-4 h-4" /> : 
                                    <ChevronRight className="w-4 h-4" />
                                  }
                                  Week {weekData.week}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{weekData.topic}</p>
                                  <Badge variant="outline" className="text-xs mt-1">{weekData.strand}</Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <ul className="text-sm space-y-1">
                                  {weekData.learningOutcomes?.slice(0, 2).map((outcome, idx) => (
                                    <li key={idx} className="flex items-start gap-1">
                                      <Target className="w-3 h-3 mt-1 text-green-600 flex-shrink-0" />
                                      <span>{outcome}</span>
                                    </li>
                                  ))}
                                  {weekData.learningOutcomes?.length > 2 && (
                                    <li className="text-gray-400 text-xs">+{weekData.learningOutcomes.length - 2} more</li>
                                  )}
                                </ul>
                              </TableCell>
                              <TableCell>
                                <ul className="text-sm space-y-1">
                                  {weekData.activities?.slice(0, 2).map((activity, idx) => (
                                    <li key={idx} className="flex items-start gap-1">
                                      <CheckSquare className="w-3 h-3 mt-1 text-blue-600 flex-shrink-0" />
                                      <span>{activity}</span>
                                    </li>
                                  ))}
                                </ul>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge className="bg-green-600">
                                  {weekData.dailyLessons?.length || 5} Days
                                </Badge>
                              </TableCell>
                            </TableRow>
                            
                            {/* Expanded Week Details */}
                            {expandedWeek === weekData.week && (
                              <TableRow>
                                <TableCell colSpan={5} className="bg-gray-50 p-0">
                                  {weekData.dailyLessons ? (
                                    <DailyLessonsView lessons={weekData.dailyLessons} week={weekData.week} />
                                  ) : (
                                    <WeekDetailsView weekData={weekData} />
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Scheme of work for {term} is being developed.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

// Week Details Fallback (when no daily lessons data)
const WeekDetailsView = ({ weekData }) => {
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <GraduationCap className="w-5 h-5 text-green-600" />
        <h4 className="font-bold text-green-800">Week {weekData.week} - {weekData.topic}</h4>
        <Badge variant="outline" className="ml-2">{weekData.strand || ''}</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {weekData.learningOutcomes && (
          <div className="bg-white rounded-lg p-3 border">
            <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Learning Outcomes</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              {(Array.isArray(weekData.learningOutcomes) ? weekData.learningOutcomes : [weekData.learningOutcomes]).map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ul>
          </div>
        )}

        {weekData.activities && (
          <div className="bg-white rounded-lg p-3 border">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Activities</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              {(Array.isArray(weekData.activities) ? weekData.activities : [weekData.activities]).map((a, i) => (
                <li key={i}>{typeof a === 'string' ? a : a.name || a.description || JSON.stringify(a)}</li>
              ))}
            </ul>
          </div>
        )}

        {weekData.resources && (
          <div className="bg-white rounded-lg p-3 border">
            <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">Resources</p>
            <p className="text-sm text-gray-700">
              {Array.isArray(weekData.resources) ? weekData.resources.join(', ') : weekData.resources}
            </p>
          </div>
        )}

        {weekData.assessment && (
          <div className="bg-white rounded-lg p-3 border">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Assessment</p>
            <p className="text-sm text-gray-700">{weekData.assessment}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Daily Lessons Component
const DailyLessonsView = ({ lessons, week }) => {
  const [selectedDay, setSelectedDay] = useState(lessons[0]?.day || 'Monday');
  
  const currentLesson = lessons.find(l => l.day === selectedDay);
  
  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <GraduationCap className="w-5 h-5 text-green-600" />
        <h4 className="font-bold text-green-800">Week {week} - Daily Lesson Plans</h4>
      </div>
      
      {/* Day Tabs */}
      <div className="flex gap-2 mb-4">
        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => {
          const lesson = lessons.find(l => l.day === day);
          return (
            <Button
              key={day}
              variant={selectedDay === day ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedDay(day)}
              className={selectedDay === day ? "bg-green-600 hover:bg-green-700" : ""}
            >
              {day.slice(0, 3)}
              {lesson && <span className="ml-1 text-xs">✓</span>}
            </Button>
          );
        })}
      </div>
      
      {/* Lesson Details */}
      {currentLesson ? (
        <Card className="border-green-200">
          <CardHeader className="pb-2 bg-green-50">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg text-green-800">{currentLesson.title}</CardTitle>
                <CardDescription className="flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> {currentLesson.day}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {currentLesson.duration}
                  </span>
                </CardDescription>
              </div>
              <Button variant="outline" size="sm">
                <Printer className="w-4 h-4 mr-2" />
                Print Lesson
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Objectives */}
              <div className="space-y-2">
                <h5 className="font-bold text-sm flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-600" />
                  Learning Objectives
                </h5>
                <ul className="text-sm space-y-1 pl-6">
                  {currentLesson.objectives?.map((obj, idx) => (
                    <li key={idx} className="list-disc">{obj}</li>
                  ))}
                </ul>
              </div>
              
              {/* Materials */}
              <div className="space-y-2">
                <h5 className="font-bold text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  Materials Needed
                </h5>
                <ul className="text-sm space-y-1 pl-6">
                  {currentLesson.materials?.map((mat, idx) => (
                    <li key={idx} className="list-disc">{mat}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <Separator className="my-4" />
            
            {/* Introduction */}
            <div className="space-y-2 mb-4">
              <h5 className="font-bold text-sm flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-yellow-600" />
                Introduction
              </h5>
              <p className="text-sm bg-yellow-50 p-3 rounded-lg">{currentLesson.introduction}</p>
            </div>
            
            {/* Main Activities */}
            <div className="space-y-2 mb-4">
              <h5 className="font-bold text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-600" />
                Main Activities
              </h5>
              <div className="space-y-2">
                {currentLesson.mainActivities?.map((activity, idx) => (
                  <div key={idx} className="bg-purple-50 p-3 rounded-lg">
                    <div className="flex justify-between items-start">
                      <span className="font-medium text-purple-800">{activity.activity}</span>
                      <Badge variant="outline" className="text-xs">{activity.duration}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Assessment */}
              <div className="space-y-2">
                <h5 className="font-bold text-sm flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-green-600" />
                  Assessment
                </h5>
                <p className="text-sm bg-green-50 p-3 rounded-lg">{currentLesson.assessment}</p>
              </div>
              
              {/* Homework */}
              <div className="space-y-2">
                <h5 className="font-bold text-sm flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-red-600" />
                  Homework
                </h5>
                <p className="text-sm bg-red-50 p-3 rounded-lg">{currentLesson.homework}</p>
              </div>
            </div>
            
            {/* Teacher Notes */}
            {currentLesson.teacherNotes && (
              <div className="mt-4 space-y-2">
                <h5 className="font-bold text-sm flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-gray-600" />
                  Teacher Notes
                </h5>
                <p className="text-sm bg-gray-100 p-3 rounded-lg italic">{currentLesson.teacherNotes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p>No lesson plan available for {selectedDay}</p>
        </div>
      )}
    </div>
  );
};

export default PrimarySchemeOfWork;
